import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { selectCounty } from "./newAppeal";
import { RestStatus } from '../commontypes/common';
import { County } from '../commontypes/types';
import { toBLQDisplayFormat } from '../pages/DefaultPages/common/modIVUtils';


interface ModIVState {
  modIVRows: string[] | null;
  modIV10Rows: string[] | null;
  modIVLoadStatus?: {
    status: RestStatus;
    error?: string;
  };
  modIV10LoadStatus?: {
    status: RestStatus;
    error?: string;
  };
  pendingTaxCourtAppeals: PendingTaxCourtAppeal[];
  pendingTaxCourtAppealLoadStatus?: {
    status: RestStatus;
    error?: string;
  };
}

const initialState: ModIVState = {
  modIVRows: null,
  modIV10Rows: null,
  pendingTaxCourtAppeals: [],
};

interface Municipality {
  key: string;
}

export interface PendingTaxCourtAppeal {
  blq: string;
  year: number;
  municipalityKey: string;
}

export const downloadModIV = createAsyncThunk<
  string,
  Municipality,
  { state: RootState }
>(
  'modIV/downloadModIV',
  async (municipality, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    const response = await fetch(`https://s3.amazonaws.com/mynjappealstorage/ModIV/${county.key}/MODIV${municipality.key}.txt`, { cache: 'no-cache' });
    return response.text();
  }
);

export const downloadModIV10 = createAsyncThunk<
  string,
  Municipality,
  { state: RootState }
>(
  'modIV/downloadModIV10',
  async (municipality: Municipality, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    const response = await fetch(`https://s3.amazonaws.com/mynjappealstorage/ModIV/${county.key}/MODIV${municipality.key}-10.txt`, { cache: 'no-cache' });
    return response.text();
  }
);

export const downloadStateAppealsForCounty = createAsyncThunk<
  PendingTaxCourtAppeal[],
  County
>(
  'modIV/downloadStateAppealsForCounty',
  async (county: County) => {
    const response = await fetch(`https://s3.amazonaws.com/mynjappealstorage/ModIV/${county.key}/${county.key}_StateAppeals.txt`, { cache: 'no-cache' });
    const text = await response.text();
    return text.split(/\r?\n/)
      .filter(line => line.trim().length > 0)
      .map(line => {
        const [internalBLQ, year] = line.split(',');
        return {
          municipalityKey: internalBLQ.substring(0, 4),
          blq: toBLQDisplayFormat(internalBLQ),
          year: parseInt(year.trim(), 10),
        };
      });
  }
);

export const modIVSlice = createSlice({
  name: 'modIV',
  initialState: initialState,
  reducers: {
    clearModIVData: (state) => {
      state.modIVRows = [];
      state.modIVLoadStatus = undefined;
      state.modIV10Rows = [];
      state.modIV10LoadStatus = undefined;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadModIV.pending, (state) => {
        state.modIVLoadStatus = { status: RestStatus.LOADING };
      })
      .addCase(downloadModIV.fulfilled, (state, action: PayloadAction<string>) => {
        state.modIVLoadStatus = { status: RestStatus.LOADED };
        state.modIVRows = action.payload.split(/\r?\n/);
      })
      .addCase(downloadModIV.rejected, (state, action) => {
        state.modIVLoadStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(downloadModIV10.pending, (state) => {
        state.modIV10LoadStatus = { status: RestStatus.LOADING };
      })
      .addCase(downloadModIV10.fulfilled, (state, action: PayloadAction<string>) => {
        state.modIV10LoadStatus = { status: RestStatus.LOADED };
        const tenholder: string[] = [];

        action.payload.split(/\r?\n/).forEach(e => {
          const prefixhold = e.substring(0, 35);
          const texthold = e.substring(35).match(/.{1,87}/g) || [];

          texthold.forEach(function (e) {
            if (e.trim().length > 0 && !e.match(/\s*E$/)) {
              tenholder.push(prefixhold + e);
            }
          });
        });

        state.modIV10Rows = tenholder.filter(e => e.replace(/ /g, '').length > 0);
      })
      .addCase(downloadModIV10.rejected, (state, action) => {
        state.modIV10LoadStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(downloadStateAppealsForCounty.pending, (state) => {
        state.pendingTaxCourtAppealLoadStatus = { status: RestStatus.LOADING };
      })
      .addCase(downloadStateAppealsForCounty.fulfilled, (state, action: PayloadAction<PendingTaxCourtAppeal[]>) => {
        state.pendingTaxCourtAppealLoadStatus = { status: RestStatus.LOADED };
        state.pendingTaxCourtAppeals = action.payload;
      })
      .addCase(downloadStateAppealsForCounty.rejected, (state, action) => {
        state.pendingTaxCourtAppealLoadStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      });
  }
});

export const { clearModIVData } = modIVSlice.actions;
export const selectModIVRows = (state: RootState) => state.modIV.modIVRows;
export const selectModIVLoadStatus = (state: RootState) => state.modIV.modIVLoadStatus?.status;
export const selectModIVLoadError = (state: RootState) => state.modIV.modIVLoadStatus?.error;
export const selectModIV10Rows = (state: RootState) => state.modIV.modIV10Rows;
export const selectModIV10LoadStatus = (state: RootState) => state.modIV.modIV10LoadStatus?.status;
export const selectModIV10LoadError = (state: RootState) => state.modIV.modIV10LoadStatus?.error;
export const selectPendingTaxCourtAppeals = (state: RootState) => state.modIV.pendingTaxCourtAppeals;
export const selectPendingTaxCourtAppealLoadStatus = (state: RootState) => state.modIV.pendingTaxCourtAppealLoadStatus?.status;
export const selectPendingTaxCourtAppealLoadError = (state: RootState) => state.modIV.pendingTaxCourtAppealLoadStatus?.error;

export default modIVSlice.reducer;